import Color from "color";
import filter from "lodash/filter";
import isEmpty from "lodash/isEmpty";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useGetColor } from "PFCore/hooks/use_get_color";
import { EconomicsScenarioDetails, Experience } from "PFTypes";
import { useTranslation } from "react-i18next";

import { RadarChart } from "./parts/radar_chart";

const MAX_SKILLS_AMOUNT = 20;

type SkillsRadarChartProps = {
  skillsRadarData: EconomicsScenarioDetails["skillsRadarData"];
  coverage: number;
  onHeaderIconClick: VoidFunction;
};

export const SkillsRadarChart = ({ skillsRadarData, coverage, onHeaderIconClick }: SkillsRadarChartProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "scenario.charts.skills" });
  const getColor = useGetColor();

  const nonSupportingSkills = skillsRadarData
    // TODO: [SP-389] Add filter by weight to get all except supporting
    .slice(0, MAX_SKILLS_AMOUNT);

  const { experienceLevels } = useExperienceConfig();

  const max = Math.max(...experienceLevels) || Experience.Advanced;

  const splitNumber = experienceLevels.length;
  const splitDistance = max / splitNumber;

  const mapExperienceToChartData = (experience: number) =>
    (experienceLevels.indexOf(experience) + 1) * splitDistance;

  const indicator = nonSupportingSkills.map(({ skill }) => ({ text: skill.value, max }));

  const skillsData = filter(
    [
      {
        value: nonSupportingSkills.map(({ requiredExperience }) =>
          mapExperienceToChartData(requiredExperience || 0)
        ),
        name: t("requested"),
        areaStyle: {
          color: Color(getColor("paletteEcharts0")).fade(0.7).toString()
        }
      },
      {
        value: nonSupportingSkills.map(({ possessedExperience }) =>
          mapExperienceToChartData(possessedExperience || 0)
        ),
        name: t("filled"),
        areaStyle: {
          color: Color(getColor("paletteEcharts1")).fade(0.7).toString()
        }
      }
    ],
    ({ value }) => !isEmpty(value)
  );

  if (!skillsData.length) {
    return null;
  }

  return (
    <RadarChart
      title={t("title")}
      coverage={coverage}
      data={skillsData}
      indicator={indicator}
      color={[getColor("paletteEcharts0"), getColor("paletteEcharts1")]}
      headerIconName="open"
      onHeaderIconClick={onHeaderIconClick}
      splitNumber={splitNumber}
    />
  );
};
